import { deleteRequest, getRequest, postRequest, putRequest } from '.'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'

export type EventActivitiesPlanningInfo = {
  activities: Record<
    string,
    {
      recommended_setup?: RecommendedSetup | null
      participant_kill_counts: Record<number, number>
      wishlist_items: Record<number, EventPlanningWishlistItem[]>
    }
  >
}

export type EventActivityPreviousSetup = {
  event: wowaudit.EventReference
  activityId: number
  totalParticipants: number
}

export type EventImportCandidate = {
  event: wowaudit.EventReference
  activities: {
    id: number
    encounterName?: string
    instanceName?: string
    thumbnailUrl?: string
    selectedCount: number
  }[]
}

export type EventChangelogEntry = {
  changedAt: string
  type: string
  change: {
    from: unknown
    to: unknown
    subject?: string | null
  }
  author: string | null
}

export type UpdateEventActivityPayload = Partial<
  Pick<wowaudit.EventActivity, 'active' | 'order' | 'planOverrides'>
> & {
  raidPlanId?: number | null
}

export type CreateEventActivitiesPayload = {
  instanceId: number
  difficulty: wowaudit.EventActivity['difficulty']
  mode:
    | 'show_instance_as_single_activity'
    | 'specific_encounter'
    | 'add_encounters_separately'
  addAt: 'beginning' | 'end'
  encounterId?: number
}

type EventPlanningWishlistItem = {
  score?: {
    percentage?: number | string
    score?: number | string
    manual?: WishLegacyPriority[]
  } | null
  id: number
  icon: string
  name: string
  alternative?: unknown
  quality: string
  difficulty: string
  edited: boolean
  age?: number | null
}

export type RoleSetup = {
  recommendation: number
  distribution: { amount: number; fraction: number }[]
}

export type RecommendedSetup = {
  tanks: RoleSetup
  healers: RoleSetup
  melee: RoleSetup
  ranged: RoleSetup
}

export type TeamOverviewEventsOptions = {
  cursor?: number
  dir?: 'before' | 'after'
  limit?: number
}

export type TeamOverviewEventsResponse = {
  events: wowaudit.EventReference[]
  nextCursor?: number
  prevCursor?: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export const getEventActivityPlanningKey = (
  activity: Pick<wowaudit.EventActivity, 'difficulty' | 'encounter'>,
) => {
  return `${activity.encounter?.id || 0}-${activity.difficulty}`
}

export const getTeamEvents = async (
  id: number,
): Promise<wowaudit.EventReference[]> => {
  return await getRequest(`/api/teams/${id}/events`)
}

export const getTeamOverviewEvents = async (
  id: number,
  options?: TeamOverviewEventsOptions,
): Promise<TeamOverviewEventsResponse> => {
  const searchParams = new URLSearchParams()
  searchParams.set('overview', 'true')
  if (options?.cursor) {
    searchParams.set('cursor', options.cursor.toString())
  }
  if (options?.dir) {
    searchParams.set('dir', options.dir)
  }
  if (options?.limit) {
    searchParams.set('limit', options.limit.toString())
  }
  return await getRequest(`/api/teams/${id}/events?${searchParams.toString()}`)
}

export const getTeamEvent = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.Event> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}`)
}

export const getEventRaidReports = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.RaidReport[]> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}/raid_reports`)
}

export const getTeamEventChangelog = async (
  teamId: number,
  eventId: number,
): Promise<{ changelog: EventChangelogEntry[] }> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}/changelog`)
}

export const updateTeamEvent = async (
  teamId: number,
  eventId: number,
  event: Partial<wowaudit.Event>,
): Promise<wowaudit.Event> => {
  return await putRequest(`/api/teams/${teamId}/events/${eventId}`, {
    event,
  })
}

export const updateEventParticipantStatus = async (
  teamId: number,
  eventParticipantId: number,
  status: wowaudit.EventParticipantReference['status'],
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: { status },
    },
  )
}

export const updateEventParticipantSignup = async (
  teamId: number,
  eventParticipantId: number,
  signup: {
    status: wowaudit.EventParticipantReference['status']
    comment?: string
    characterId?: number
    role?: string
    specId?: number | null
  },
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: signup,
    },
  )
}

export const createEventParticipant = async (
  teamId: number,
  eventParticipant: {
    eventId: number
    characterId: number
    addedAsGuest: boolean
    role?: string
    specId?: number
    status?: wowaudit.EventParticipantReference['status']
    comment?: string
  },
): Promise<wowaudit.Event> => {
  return await postRequest(`/api/teams/${teamId}/event_participants`, {
    eventParticipant,
  })
}

export const createViewerEventParticipant = async (
  teamId: number,
  eventParticipant: {
    eventId: number
    characterId: number
    role: string
    status: wowaudit.EventParticipantReference['status']
    comment?: string
  },
): Promise<wowaudit.Event> => {
  return await createEventParticipant(teamId, {
    ...eventParticipant,
    addedAsGuest: true,
  })
}

export const deleteEventParticipant = async (
  teamId: number,
  eventParticipantId: number,
): Promise<wowaudit.Event> => {
  return await deleteRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
  )
}

export const updateEventParticipantSelection = async (
  teamId: number,
  eventParticipantId: number,
  selection: {
    activityId?: number
    selected?: boolean
    role?: string
    specId?: number | null
    characterId?: number
  },
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      eventParticipant: {
        selection,
      },
    },
  )
}

export const bulkUpdateEventActivitySelection = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  selected: boolean,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/bulk_selection`,
    {
      eventActivity: { selected },
    },
  )
}

export const updateEventActivity = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  eventActivity: UpdateEventActivityPayload,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}`,
    {
      event_activity: eventActivity,
    },
  )
}

export const exportEventActivityOverrides = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<wowaudit.Event> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/export_overrides`,
    {},
  )
}

export const createEventActivities = async (
  teamId: number,
  eventId: number,
  eventActivity: CreateEventActivitiesPayload,
): Promise<wowaudit.Event> => {
  return await postRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities`,
    {
      eventActivity,
    },
  )
}

export const deleteEventActivity = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<wowaudit.Event> => {
  return await deleteRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}`,
  )
}

export const copyEventActivitySelections = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
  sourceActivityId: number,
  sourceEventId?: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/copy_selections`,
    {
      eventActivity: {
        sourceActivityId,
        sourceEventId,
      },
    },
  )
}

export const getEventActivityPreviousSetups = async (
  teamId: number,
  eventId: number,
  eventActivityId: number,
): Promise<{
  previousSetups: EventActivityPreviousSetup[]
}> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/${eventActivityId}/previous_setups`,
  )
}

export const updateEventActivityOrder = async (
  teamId: number,
  eventId: number,
  activityOrder: number[],
): Promise<wowaudit.Event> => {
  return await putRequest(`/api/teams/${teamId}/events/${eventId}`, {
    event: { activityOrder },
  })
}

export const getEventImportCandidates = async (
  teamId: number,
  eventId: number,
): Promise<{ importCandidates: EventImportCandidate[] }> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/import_candidates`,
  )
}

export const importEventFromPreviousEvent = async (
  teamId: number,
  eventId: number,
  sourceEventId: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/import_from_event`,
    {
      event: { sourceEventId },
    },
  )
}

export const resetEventSelections = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.Event> => {
  return await putRequest(
    `/api/teams/${teamId}/events/${eventId}/reset_selections`,
    {},
  )
}

export const getEventActivitiesPlanningInfo = async (
  teamId: number,
  eventId: number,
): Promise<EventActivitiesPlanningInfo> => {
  return await getRequest(
    `/api/teams/${teamId}/events/${eventId}/event_activities/planning_info`,
  )
}
